import type {
    ICampaignLocationAndLanguage,
    ILocationDetailsToPresent,
    ISupportedLanguagesAndLocale,
    ISupportedLocationByGoogle,
} from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import {
    forbiddenLanguages,
    forbiddenLocations,
    NON_VALID_LOCATION_TYPES,
    SORT_ORDER,
} from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import { fetchLocations, getLanguagesList } from "app/components/campaigns/campaigns-wizard/campaignsWizardFetch";
import CountryService from "app/services/CountryService";
import queryClient from "single-spa/react-app/query-client";

const RANK_TRACKING_LOCALE_CONFIG_KEY = "RANK_TRACKING_LOCALE_CONFIG";

const getLanguagesQueryKey = (isDemoCampaign: boolean) => ["campaigns", "languages", isDemoCampaign] as const;
const getLocationsQueryKey = (isDemoCampaign: boolean) => ["campaigns", "locations", isDemoCampaign] as const;

// Catalogs rarely change. fetchQuery has no observers, so default 15m gcTime would drop the cache.
const CATALOG_QUERY_CACHE = { staleTime: Infinity, gcTime: Infinity } as const;

export const getLocationNameToPresent = (name: string): string => {
    return name?.replaceAll(",", ", ");
};

export const getLocationNameToSave = (name: string): string => {
    return name?.replaceAll(", ", ",");
};

export const getSortedLocations = (locations: ISupportedLocationByGoogle[] = []): ILocationDetailsToPresent[] => {
    return locations
        .reduce<ILocationDetailsToPresent[]>((result, location) => {
            const isValidLocationType = NON_VALID_LOCATION_TYPES.every(
                (nonValidLocation) => location.type.toString() !== nonValidLocation,
            );
            const isValidLocation = forbiddenLocations.every((forbiddenLocation) => {
                if (forbiddenLocation.includes("*")) {
                    const partialForbiddenLocation = forbiddenLocation.replace("*", "");
                    return !location.canonicalName.includes(partialForbiddenLocation);
                } else {
                    return location.canonicalName !== forbiddenLocation;
                }
            });
            const shouldAddLocation = isValidLocation && isValidLocationType;
            if (shouldAddLocation) {
                const selectedCountry = CountryService.countries.find(
                    (country) => location.countryCode.toLowerCase() === country.code,
                );
                result.push({
                    id: selectedCountry?.id,
                    text: location.canonicalName,
                    type: location.type,
                });
            }
            return result;
        }, [])
        .sort((a, b) => {
            return SORT_ORDER.indexOf(a.type) - SORT_ORDER.indexOf(b.type);
        });
};

export const getLanguagesForCampaigns = async (isDemoCampaign: boolean): Promise<ISupportedLanguagesAndLocale[]> => {
    // TODO: remove this after Jan 2027. No need to keep removing cached data forever.
    // Remove previously persisted languages from localStorage, since now
    // we keep them in in-memory QueryClient cache
    localStorage.removeItem(RANK_TRACKING_LOCALE_CONFIG_KEY);

    return queryClient.fetchQuery({
        queryKey: getLanguagesQueryKey(isDemoCampaign),
        ...CATALOG_QUERY_CACHE,
        queryFn: async () => {
            const languagesList = await getLanguagesList(isDemoCampaign);
            return (languagesList ?? []).filter((language) =>
                forbiddenLanguages.every((forbiddenLanguage) => !language.description.includes(forbiddenLanguage)),
            );
        },
    });
};

export const getLocationsForCampaigns = async (isDemoCampaign: boolean): Promise<ILocationDetailsToPresent[]> => {
    return queryClient.fetchQuery({
        queryKey: getLocationsQueryKey(isDemoCampaign),
        ...CATALOG_QUERY_CACHE,
        queryFn: async () => {
            const locations = await fetchLocations(isDemoCampaign);
            return getSortedLocations(locations ?? []);
        },
    });
};

export const getLanguagesAndLocationsForCampaigns = async (
    isDemoCampaign: boolean,
): Promise<ICampaignLocationAndLanguage> => {
    const promises: Promise<ISupportedLanguagesAndLocale[] | ILocationDetailsToPresent[]>[] = [
        getLanguagesForCampaigns(isDemoCampaign),
        getLocationsForCampaigns(isDemoCampaign),
    ];
    const [languagesListResult, locationsResult] = await Promise.allSettled(promises);
    const languagesList = (languagesListResult as PromiseFulfilledResult<ISupportedLanguagesAndLocale[]>)?.value;
    const locationsList = (locationsResult as PromiseFulfilledResult<ILocationDetailsToPresent[]>)?.value;
    return {
        languagesList,
        locationsList,
    };
};
